import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Eye, RefreshCw, Trash2, Loader2, FilePlus2 } from "lucide-react";

const fmtSize = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
};

export default function DocumentManager({ opportunityId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const addRef = useRef(null);
  const replaceRefs = useRef({});

  const load = () =>
    api.get(`/opportunities/${opportunityId}/documents`)
      .then((r) => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [opportunityId]);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action impossible.");

  const upload = async (file) => {
    if (!file) return;
    setBusy("add");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/opportunities/${opportunityId}/documents/upload`, fd);
      toast.success("Document PDF ajouté.");
      await load();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const replace = async (docId, file) => {
    if (!file) return;
    setBusy(`r-${docId}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.put(`/opportunities/${opportunityId}/documents/${docId}`, fd);
      toast.success("Document remplacé.");
      await load();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const remove = async (docId) => {
    if (!window.confirm("Supprimer définitivement ce document ?")) return;
    setBusy(`d-${docId}`);
    try {
      await api.delete(`/opportunities/${opportunityId}/documents/${docId}`);
      toast.success("Document supprimé.");
      await load();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const download = async (doc) => {
    setBusy(`dl-${doc.id}`);
    try {
      const res = await api.get(`/files/${doc.storage_path}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename || "document.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  return (
    <div data-testid="document-manager">
      <input ref={addRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="document-add-input"
             onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="document-loading"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : docs.length === 0 ? (
        <button type="button" data-testid="document-add-btn" disabled={busy === "add"} onClick={() => addRef.current?.click()}
                className="flex h-28 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 transition-colors hover:border-[#C6A87C] hover:text-[#C6A87C]">
          {busy === "add" ? <Loader2 className="h-6 w-6 animate-spin" /> : <FilePlus2 className="h-6 w-6" />}
          <span className="text-sm font-medium">Joindre un document PDF</span>
          <span className="text-xs">Pitch deck, plans, dossier… (25 Mo max)</span>
        </button>
      ) : (
        <div className="space-y-2">
          {docs.map((doc, i) => (
            <div key={doc.id} data-testid={`document-${i}`}
                 className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-[#C6A87C]">
              <span className="flex min-w-0 items-center gap-3 text-sm text-slate-700">
                <FileText className="h-5 w-5 shrink-0 text-[#C6A87C]" />
                <span className="truncate" data-testid={`document-name-${i}`}>{doc.filename}</span>
                <span className="shrink-0 text-xs text-slate-400">{fmtSize(doc.size)}</span>
              </span>
              <div className="flex shrink-0 items-center gap-1.5">
                <a href={fileUrl(doc.storage_path)} target="_blank" rel="noopener noreferrer" title="Consulter"
                   data-testid={`document-view-${i}`} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]">
                  <Eye className="h-4 w-4" />
                </a>
                <button type="button" onClick={() => download(doc)} title="Télécharger" disabled={busy === `dl-${doc.id}`}
                   data-testid={`document-download-${i}`} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]">
                  {busy === `dl-${doc.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                </button>
                <button type="button" title="Remplacer" data-testid={`document-replace-${i}`} disabled={busy === `r-${doc.id}`}
                        onClick={() => replaceRefs.current[doc.id]?.click()}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]">
                  {busy === `r-${doc.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </button>
                <button type="button" title="Supprimer" data-testid={`document-delete-${i}`} disabled={busy === `d-${doc.id}`}
                        onClick={() => remove(doc.id)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 hover:bg-red-50">
                  {busy === `d-${doc.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
                <input ref={(el) => (replaceRefs.current[doc.id] = el)} type="file" accept="application/pdf,.pdf" className="hidden"
                       data-testid={`document-replace-input-${i}`}
                       onChange={(e) => { if (e.target.files?.[0]) replace(doc.id, e.target.files[0]); e.target.value = ""; }} />
              </div>
            </div>
          ))}
          <button type="button" data-testid="document-add-btn" disabled={busy === "add"} onClick={() => addRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 py-3 text-sm font-medium text-slate-400 transition-colors hover:border-[#C6A87C] hover:text-[#C6A87C]">
            {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />} Ajouter un PDF
          </button>
        </div>
      )}
    </div>
  );
}
